import { useCallback, useMemo } from 'react'

import { useDebounceValue } from 'usehooks-ts'

import { SearchNetworkSettings } from '@tribeplatform/gql-client/types'

import { useQuerySmartSearch } from '../../hooks/useQuerySmartSearch.js'
import {
  ResultsView,
  SmartSearchEntityType,
  SmartSearchFilters,
} from '../../SmartSearch.types.js'

import { NoResults } from './NoResults.js'
import { SmartSearchResultsSection } from './SmartSearchResultsSection.js'

interface Props {
  filters: SmartSearchFilters
  setFilters: (filters: SmartSearchFilters) => void
  view: ResultsView
  searchNetworkSettings?: SearchNetworkSettings
}

/**
 * @CAUTION A note for future travelers:
 * The search component structure is an utter work of abomination!
 * Be very cautious on how you rely on types. For example, the filter.type offers an OFFICIAL_POSTS value which is not accepted by
 * the backend and is only intended to differentiate the official posts from the regular posts in the UI.
 * However, it is also involved in the data fetching hook and is used to categorize the posts into different groups.
 *
 * On the other hand, there’s also an ExtendedSmartSearchEntityType which adds the `HISTORY` value to the SmartSearchEntityType enum which is
 * used to render the additional search history section in the UI.
 *
 * Even the namings are all wrong and misleading! So tread carefully when applying minor.
 *
 * For bigger changes, you may need to a proper ground up rework of the search module if you don’t want to
 * footgun yourself.
 */

export const AiPoweredSearchResults = ({
  filters,
  setFilters,
  view,
  searchNetworkSettings,
}: Props) => {
  const { query, type: typeFilter, spaceIds } = filters
  const [debouncedQuery] = useDebounceValue(query, 500)

  const {
    officialPostsQuery,
    postsQuery,
    membersQuery,
    spacesQuery,
    externalsSearchQuery,
    hasResultsByType,
  } = useQuerySmartSearch({
    query: debouncedQuery,
    date: filters.date,
    tagsId: filters.tagsId,
    type: typeFilter,
    spaceIds,
    officialResourcesSpaceIds:
      searchNetworkSettings?.officialResourcesSpaceIds || [],
  })

  const isSearchOnTypeAllowed = useCallback(
    (type: SmartSearchEntityType) => {
      const isLimitedSearchScope = !!typeFilter
      if (!isLimitedSearchScope) {
        return true
      }

      // Handle official posts: both POSTS and OFFICIAL_POSTS should be allowed when filtering by posts
      if (typeFilter === SmartSearchEntityType.POSTS) {
        return (
          type === SmartSearchEntityType.POSTS ||
          type === SmartSearchEntityType.OFFICIAL_POSTS
        )
      }

      return type === typeFilter
    },
    [typeFilter],
  )

  const items = useMemo(
    () => [
      {
        type: SmartSearchEntityType.OFFICIAL_POSTS,
        queryResult: officialPostsQuery,
        shouldShow:
          isSearchOnTypeAllowed(SmartSearchEntityType.POSTS) &&
          searchNetworkSettings?.officialResourcesSpaceIds?.length > 0,
      },
      {
        type: SmartSearchEntityType.POSTS,
        queryResult: postsQuery,
        shouldShow: isSearchOnTypeAllowed(SmartSearchEntityType.POSTS),
      },
      {
        type: SmartSearchEntityType.SPACES,
        queryResult: spacesQuery,
        shouldShow:
          isSearchOnTypeAllowed(SmartSearchEntityType.SPACES) &&
          searchNetworkSettings?.availableSearchSources?.spaces,
      },
      {
        type: SmartSearchEntityType.MEMBERS,
        queryResult: membersQuery,
        shouldShow:
          isSearchOnTypeAllowed(SmartSearchEntityType.MEMBERS) &&
          searchNetworkSettings?.availableSearchSources?.members,
      },
      {
        type: SmartSearchEntityType.EXTERNALS,
        queryResult: externalsSearchQuery,
        shouldShow: true,
      },
    ],
    [
      externalsSearchQuery,
      isSearchOnTypeAllowed,
      membersQuery,
      officialPostsQuery,
      postsQuery,
      searchNetworkSettings?.availableSearchSources?.members,
      searchNetworkSettings?.availableSearchSources?.spaces,
      searchNetworkSettings?.officialResourcesSpaceIds?.length,
      spacesQuery,
    ],
  )

  if (!query || !searchNetworkSettings) {
    return null
  }

  const showNoResults = hasResultsByType(typeFilter) === false

  const handleSwitchToSolely = (type: SmartSearchEntityType) => {
    /**
     * @CAUTION we set the expanded parameter to focus on the specific section
     * Always use normalized type (posts for both official and regular posts)
     */
    const normalizedType =
      type === SmartSearchEntityType.OFFICIAL_POSTS
        ? SmartSearchEntityType.POSTS
        : type

    setFilters({ ...filters, type: normalizedType })
  }

  if (showNoResults) {
    return <NoResults query={query} type={typeFilter} />
  }

  return (
    <>
      {items.map(item =>
        item.shouldShow ? (
          <SmartSearchResultsSection
            key={item.type}
            type={item.type}
            queryResult={item.queryResult}
            query={query}
            view={view}
            spaceIds={spaceIds}
            onSwitchToSolely={handleSwitchToSolely}
          />
        ) : null,
      )}
    </>
  )
}
